import { Box, Container, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { translations } from "../translations";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

const PrivacyContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "var(--background-white)",
  minHeight: "100vh",
  paddingTop: theme.spacing(18),
  paddingBottom: theme.spacing(8),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(12.5),
  },
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  width: "calc(100% - 16px)",
  maxWidth: "1200px",
  padding: "0 !important",
  wordWrap: "break-word",
  overflowWrap: "break-word",
  wordBreak: "break-word",
  "& h1": {
    marginBottom: theme.spacing(1.5),
    color: "var(--text-primary)",
    ...theme.typography.h2,
  },
  "& h2": {
    marginBottom: 0,
    color: "var(--text-primary)",
    ...theme.typography.h3,
  },
  "& p": {
    color: "var(--text-primary)",
    ...theme.typography.body1,
  },
  "& a": {
    color: "var(--text-primary)",
    textDecoration: "underline",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  color: "var(--text-primary)",
  fontWeight: 400,
  textTransform: "none",
  ...theme.typography.body1,
  padding: 0,
  minWidth: "auto",
  "&:hover": {
    backgroundColor: "transparent",
  },
}));

const CreatedDate = styled(Typography)(({ theme }) => ({
  color: "var(--text-primary)",
  ...theme.typography.body1,
  marginBottom: theme.spacing(8),
}));

const BaseCard = styled(Box)(({ theme }) => ({
  backgroundColor: "var(--overlay-very-light)",
  borderRadius: "40px",
  padding: "40px",
  width: "calc(100%)",
  maxWidth: "1200px",
  wordWrap: "break-word",
  overflowWrap: "break-word",
  wordBreak: "break-word",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("md")]: {
    padding: "24px",
    borderRadius: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px",
    borderRadius: "16px",
    margin: "0 8px",
  },
}));

const SectionCard = styled(BaseCard)(({ theme }) => ({
  gap: theme.spacing(2),
}));

const SectionsContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  alignItems: "center",
}));

const TextBase = styled(Typography)(({ theme }) => ({
  color: "var(--text-primary)",
  ...theme.typography.body1,
  wordWrap: "break-word",
  overflowWrap: "break-word",
}));

const ParagraphText = styled(TextBase)(() => ({
  marginBottom: 0,
  fontWeight: "400 !important",
}));

const SemiboldParagraphText = styled(TextBase)(() => ({
  marginBottom: 0,
  fontWeight: "600 !important",
}));

const IntroText = styled(TextBase)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const BulletList = styled("ul")(({ theme }) => ({
  margin: 0,
  marginTop: theme.spacing(2), // Add spacing between text and list
  paddingLeft: theme.spacing(3),
  listStyleType: "disc",
  "& li": {
    color: "var(--text-primary)",
    ...theme.typography.body1,
    marginBottom: 0,
  },
}));

// Helper function to format definitions with bold term
const formatDefinition = (text: string) => {
  // Find the pattern "Term is" (English) or "Term –" (Russian)
  const matchEn = text.match(/^([^:]+?)(\s+is\s+)/i);
  const matchRu = text.match(/^([^:]+?)(\s+–\s+)/);

  const match = matchEn || matchRu;

  if (match) {
    const term = match[1];
    const separator = match[2];
    const rest = text.substring(match[0].length);

    return (
      <>
        <strong style={{ fontWeight: 600 }}>{term}</strong>
        {separator}
        {rest}
      </>
    );
  }

  return text;
};

export default function PrivacyPolicy() {
  const t = translations;
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBackClick = () => {
    navigate("/");
  };

  const getContent = (baseKey: string, maxCount: number = 20): string[] => {
    const content: string[] = [];
    for (let i = 1; i <= maxCount; i++) {
      const key = `${baseKey}Text${i}`;
      const value = (t as any)[key];
      if (value) {
        content.push(value);
      }
    }
    return content;
  };

  const getContentWithSubpoints = (
    baseKey: string,
    maxCount: number = 20
  ): string[] => {
    const content: string[] = [];
    for (let i = 1; i <= maxCount; i++) {
      const key = `${baseKey}Text${i}`;
      const value = (t as any)[key];
      if (value) {
        content.push(value);
        // Check for subpoints like Text3_1, Text3_2, etc.
        for (let j = 1; j <= 10; j++) {
          const subKey = `${key}_${j}`;
          const subValue = (t as any)[subKey];
          if (subValue) {
            content.push(subValue);
          } else {
            break; // No more subpoints for this item
          }
        }
      }
    }
    return content;
  };

  const sections = [
    {
      title: t.termsAndDefinitions,
      content: [
        (t as any).termsDefinitionsService,
        (t as any).termsDefinitionsDataSubject,
        (t as any).termsDefinitionsPersonalData,
        (t as any).termsDefinitionsProcessing,
        (t as any).termsDefinitionsAutomatedProcessing,
        (t as any).termsDefinitionsProvision,
        (t as any).termsDefinitionsBlocking,
        (t as any).termsDefinitionsDestruction,
        (t as any).termsDefinitionsDepersonalization,
        (t as any).termsDefinitionsCrossBorder,
        (t as any).termsDefinitionsOperator,
      ].filter(Boolean),
    },
    {
      title: t.generalProvisions,
      content: getContent("generalProvisions", 7),
    },
    {
      title: t.keyRightsObligations,
      content: [
        (t as any).operatorRights,
        ...getContent("operatorRights", 6),
        (t as any).operatorObligations,
        ...getContent("operatorObligations", 6),
        (t as any).dataSubjectRights,
        ...getContent("dataSubjectRights", 5),
        (t as any).dataSubjectObligations,
        ...getContent("dataSubjectObligations", 4),
      ].filter(Boolean),
    },
    {
      title: t.principlesProcessing,
      content: getContent("principlesProcessing", 7),
    },
    {
      title: (t as any).purposesCollection,
      content: getContentWithSubpoints("purposesCollection", 11),
    },
    {
      title: (t as any).procedureConditions,
      content: getContentWithSubpoints("procedureConditions", 18),
    },
    {
      title: (t as any).updatingCorrectionDeletion,
      content: getContent("updatingCorrectionDeletion", 9),
    },
    {
      title: (t as any).protectionMeasures,
      content: getContentWithSubpoints("protectionMeasures", 4),
    },
    {
      title: t.operatorDetails,
      content: [],
      isOperatorDetails: true,
    },
  ];

  return (
    <PrivacyContainer>
      <ContentContainer>
        <BackButton onClick={handleBackClick}>{t.back}</BackButton>

        <Typography variant="h3" component="h1">
          {t.privacyPolicyTitle}
        </Typography>

        <CreatedDate>{t.privacyPolicyCreated}</CreatedDate>

        <IntroText variant="body1">{t.privacyPolicyIntro}</IntroText>

        <SectionsContainer>
          {sections.map((section, index) => (
            <SectionCard key={index}>
              <Typography variant="h4" component="h2">
                {section.title}
              </Typography>
              {(section as any).isOperatorDetails ? (
                <Box>
                  <ParagraphText variant="body1">
                    <strong>{(t as any).operatorDetailsName}</strong>{" "}
                    {(t as any).operatorDetailsNameValue}
                  </ParagraphText>
                  <ParagraphText variant="body1">
                    <strong>{(t as any).operatorDetailsDate}</strong>{" "}
                    {(t as any).operatorDetailsDateValue}
                  </ParagraphText>
                  <ParagraphText variant="body1">
                    <strong>{(t as any).operatorDetailsRegistration}</strong>{" "}
                    {(t as any).operatorDetailsRegistrationValue}
                  </ParagraphText>
                  <ParagraphText variant="body1">
                    <strong>{(t as any).operatorDetailsLicense}</strong>{" "}
                    {(t as any).operatorDetailsLicenseValue}
                  </ParagraphText>
                  <ParagraphText variant="body1">
                    <strong>{(t as any).operatorDetailsOffice}</strong>{" "}
                    {(t as any).operatorDetailsOfficeValue}
                  </ParagraphText>
                </Box>
              ) : (
                section.content.map((paragraph, pIndex) => {
                  // Check if there's a list for this paragraph
                  // Extract the key like "operatorObligationsText5" or "purposesCollectionText2" from the paragraph
                  let listKey = null;

                  // Try to match patterns - check for 4-level first, then 3-level, then 2-level
                  const match4Digits = paragraph.match(
                    /^(\d+\.\d+\.\d+\.\d+)\./
                  );
                  const match3Digits = paragraph.match(/^(\d+\.\d+\.\d+)\./);
                  const match2Digits = paragraph.match(/^(\d+\.\d+)\./);

                  const match = match4Digits || match3Digits || match2Digits;

                  if (match) {
                    const number = match[1];
                    // Search for the corresponding key in translations
                    const keys = Object.keys(t);
                    const textKey = keys.find((key) => {
                      const value = (t as any)[key];
                      return (
                        typeof value === "string" && value.startsWith(number)
                      );
                    });

                    if (textKey) {
                      listKey = `${textKey}List`;
                    }
                  }

                  const list = listKey ? (t as any)[listKey] : null;

                  // Check if this is a 3-level numbered item in section 4 (e.g., 4.2.1, 4.2.2) but NOT 4-level (4.2.1.1)
                  // Only apply semibold to section 4 items
                  let isSection4ThreeLevelItem = false;
                  if (match3Digits && !match4Digits) {
                    const number = match3Digits[1];
                    const dotCount = (number.match(/\./g) || []).length;
                    // Check if it's section 4 and has exactly 2 dots (4.2.1)
                    if (dotCount === 2 && number.startsWith("4.")) {
                      isSection4ThreeLevelItem = true;
                    }
                  }

                  const TextComponent = isSection4ThreeLevelItem
                    ? SemiboldParagraphText
                    : ParagraphText;

                  return (
                    <Box key={pIndex}>
                      <TextComponent variant="body1">
                        {index === 0 ? formatDefinition(paragraph) : paragraph}
                      </TextComponent>
                      {list && Array.isArray(list) && (
                        <BulletList>
                          {list.map((item: string, itemIndex: number) => (
                            <li key={itemIndex}>{item}</li>
                          ))}
                        </BulletList>
                      )}
                    </Box>
                  );
                })
              )}
            </SectionCard>
          ))}
        </SectionsContainer>
      </ContentContainer>
    </PrivacyContainer>
  );
}
